import { Component, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';

/** One line per candidate in a bulk batch (message, fire, approve, deny). */
export interface BulkResultRow {
  name: string;
  success: boolean;
  error?: string;
}

export interface BulkResultsSummaryData {
  /** Past-tense verb for the headline, e.g. "Message sent", "Fired". */
  headline: string;
  results: BulkResultRow[];
}

/**
 * MP-5694 (item 4): "N succeeded, M failed" summary after a bulk batch, listing the
 * failures with their reasons. Opened via DialogService; closes with no payload.
 *
 * Signal-based: the dialog data is captured once into a signal and everything the
 * template shows is a `computed` over it.
 */
@Component({
  selector: 'app-bulk-results-summary',
  standalone: true,
  imports: [CommonModule, ButtonModule, DynamicDialogModule],
  templateUrl: './bulk-results-summary.component.html',
  styleUrl: './bulk-results-summary.component.scss'
})
export class BulkResultsSummaryComponent {
  private readonly data = signal<BulkResultsSummaryData>({ headline: '', results: [] });

  readonly headline = computed(() => this.data().headline ?? '');
  readonly succeeded = computed(() => (this.data().results ?? []).filter(r => r.success));
  readonly failed = computed(() => (this.data().results ?? []).filter(r => !r.success));
  readonly summaryText = computed(() => `${this.succeeded().length} succeeded, ${this.failed().length} failed`);

  constructor(private ref: DynamicDialogRef, config: DynamicDialogConfig<BulkResultsSummaryData>) {
    if (config.data) {
      this.data.set(config.data);
    }
  }

  close(): void {
    this.ref.close();
  }
}
